import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { WhButtonComponent } from '@wheere-front/shared-ui';
import { GeneratedSignalView, signalJsonText } from '../util/frequency-file';
import { zipStored } from '../util/zip-store';
import { SignalJsonDialogComponent } from './frequency-dialogs.components';
import { AntennaTagComponent, downloadText } from './frequency-ui.components';
import { SiteConfigurationService } from './site-configuration.service';
import { SiteFrequencyStore } from './site-frequency.store';

/**
 * Carte « signal.json générés » de l'onglet Configuration (inside, ADR-039) : un par gateway,
 * recalculé à chaque changement ; quelle antenne remplit chaque DAC, aperçu et export (MA,
 * configuration en direct) sans amp_div, encart des topics publiés à la validation.
 */
@Component({
  selector: 'lib-generated-signals',
  standalone: true,
  imports: [TranslocoModule, WhButtonComponent, AntennaTagComponent, SignalJsonDialogComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section
      *transloco="let t"
      class="shrink-0 rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
      <header class="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5 dark:border-slate-700">
        <span class="text-eletricPurple-500 dark:text-eletricPurple-300"><i class="pi pi-microchip text-sm"></i></span>
        <h3 class="text-base font-semibold text-gray-800 dark:text-slate-100">
          {{ t('siteConfiguration.frequency.generatedTitle') }}
          <span class="ml-1 text-sm font-normal text-gray-400 dark:text-slate-500">({{ signals()?.length ?? 0 }})</span>
        </h3>
        <div class="flex-1"></div>
        @if (signals()?.length) {
          <wh-button
            variant="ghost"
            size="xs"
            userClass="text-blue-600 dark:text-blue-400"
            [attr.title]="t('siteConfiguration.frequency.zipTitle')"
            (click)="exportZip()">
            <i class="pi pi-box mr-1 text-xs"></i>.zip
          </wh-button>
        }
      </header>
      <div class="flex flex-col gap-2 p-3">
        <p class="text-[11px] text-gray-500 dark:text-slate-400">
          {{ t('siteConfiguration.frequency.generatedIntro') }}
        </p>
        @if (signals(); as list) {
          @for (g of list; track g.gatewayIndex) {
            <div
              data-testid="generated-signal"
              class="rounded-lg border p-2.5"
              [class]="hasError(g) ? 'border-red-300 dark:border-red-500/50' : 'border-gray-200 dark:border-slate-700'">
              <div class="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span
                  class="inline-block size-2.5 shrink-0 rounded-full"
                  [class]="hasError(g) ? 'bg-red-400' : 'bg-green-500'"></span>
                <span class="text-sm font-semibold text-gray-800 dark:text-slate-100"
                  >GW{{ g.gatewayIndex }} · {{ g.gatewayName }}</span
                >
                <span class="text-[11px] tabular-nums text-gray-500 dark:text-slate-400">{{
                  t('siteConfiguration.frequency.entries', { count: g.entries })
                }}</span>
                <span class="ml-auto flex items-center gap-0.5">
                  <button
                    type="button"
                    class="inline-flex size-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-700"
                    [title]="t('siteConfiguration.frequency.previewSignal')"
                    [attr.aria-label]="t('siteConfiguration.frequency.previewSignal')"
                    (click)="preview.set(g)">
                    <i class="pi pi-eye text-sm"></i>
                  </button>
                  <button
                    type="button"
                    class="inline-flex size-8 items-center justify-center rounded-md text-blue-600 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-30 dark:text-blue-400 dark:hover:bg-blue-500/10"
                    [disabled]="g.conflicts.length > 0"
                    [title]="t('siteConfiguration.frequency.exportSignal')"
                    [attr.aria-label]="t('siteConfiguration.frequency.exportSignal')"
                    (click)="download(g)">
                    <i class="pi pi-cloud-download text-sm"></i>
                  </button>
                </span>
              </div>
              <div class="grid grid-cols-4 gap-1">
                @for (d of dacs; track d) {
                  <div
                    class="rounded-md border px-1.5 py-1"
                    [class]="
                      count(g, d)
                        ? 'border-gray-200 bg-white dark:border-slate-600 dark:bg-slate-800'
                        : 'border-dashed border-gray-200 dark:border-slate-700'
                    ">
                    <div class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500">
                      dac{{ d }}
                    </div>
                    <div
                      class="mt-0.5 flex items-center gap-1 text-[11px] tabular-nums text-gray-700 dark:text-slate-200">
                      @if (count(g, d) && g.dacAntenna[d] !== null) {
                        <lib-antenna-tag [index]="g.dacAntenna[d]!" />
                        <span class="text-gray-500 dark:text-slate-400">{{ count(g, d) }}</span>
                      } @else {
                        <span class="text-gray-300 dark:text-slate-600">{{
                          t('siteConfiguration.frequency.empty')
                        }}</span>
                      }
                    </div>
                  </div>
                }
              </div>
              @for (c of g.conflicts; track $index) {
                <div class="mt-1.5 text-[11px] font-medium text-red-600 dark:text-red-400">
                  {{
                    t('siteConfiguration.frequency.blockers.DAC_SHARED', {
                      first: 'A' + (c.first + 1),
                      second: 'A' + (c.second + 1),
                      gateway: 'GW' + g.gatewayIndex,
                      dac: c.dac,
                    })
                  }}
                </div>
              }
              @for (a of g.missingDelta; track a) {
                <div class="mt-1.5 text-[11px] font-medium text-red-600 dark:text-red-400">
                  {{ t('siteConfiguration.frequency.missingDeltaDac', { antenna: 'A' + (a + 1) }) }}
                </div>
              }
            </div>
          }
        } @else {
          <div
            class="rounded-lg border border-dashed border-red-200 py-8 text-center text-xs text-red-500 dark:border-red-500/40">
            {{ t('siteConfiguration.frequency.nothingGenerated') }}
          </div>
        }
        <div
          class="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300">
          <i class="pi pi-send mt-0.5 text-xs"></i>
          <div class="min-w-0 flex-1">
            <b>{{ t('siteConfiguration.frequency.publishTitle') }}</b>
            <div class="mt-1 grid gap-0.5 font-mono text-[11px]">
              <span
                >gw/v3/&#123;deviceId&#125;/to_gw/signal
                <span class="font-sans text-gray-500 dark:text-slate-400"
                  >— {{ t('siteConfiguration.frequency.topicSignal') }}</span
                ></span
              >
              <span
                >city/v3/&#123;siteId&#125;/config
                <span class="font-sans text-gray-500 dark:text-slate-400"
                  >— {{ t('siteConfiguration.frequency.topicConfig') }}</span
                ></span
              >
              <span
                >city/v3/&#123;siteId&#125;/config_delta
                <span class="font-sans text-gray-500 dark:text-slate-400"
                  >— {{ t('siteConfiguration.frequency.topicConfigDelta') }}</span
                ></span
              >
            </div>
            <span class="mt-1 block">{{ t('siteConfiguration.frequency.firmwareNote') }}</span>
          </div>
        </div>
      </div>
    </section>

    @if (preview(); as p) {
      <lib-signal-json-dialog [signal]="p" [sizeLabel]="sizeLabel()" (closed)="preview.set(null)" />
    }
  `,
})
export class GeneratedSignalsComponent {
  protected readonly store = inject(SiteFrequencyStore);
  readonly #svc = inject(SiteConfigurationService);
  protected readonly dacs = [0, 1, 2, 3] as const;
  protected readonly signals = this.store.generated;
  protected readonly preview = signal<GeneratedSignalView | null>(null);
  protected readonly sizeLabel = computed(() => this.#svc.selectedSize()?.label ?? '');

  protected count(g: GeneratedSignalView, d: number): number {
    return [g.signal.dac0, g.signal.dac1, g.signal.dac2, g.signal.dac3][d].length;
  }

  protected hasError(g: GeneratedSignalView): boolean {
    return g.conflicts.length > 0 || g.missingDelta.length > 0;
  }

  protected download(g: GeneratedSignalView): void {
    downloadText(g.fileName, signalJsonText(g.signal));
  }

  protected exportZip(): void {
    const list = this.signals() ?? [];
    const entries: Record<string, string> = {};
    for (const g of list) {
      if (!g.conflicts.length) entries[g.fileName] = signalJsonText(g.signal);
    }
    const zip = zipStored(entries);
    downloadText(`${this.store.slug() || 'site'}_signal.zip`, new Blob([zip], { type: 'application/zip' }));
  }
}
